import React from "react";
import DivContainer from "../../components/layouts/DivContainer";
import { BtnGeneral } from "../../styles/Buttons";
import useCitologia from "../../hooks/citologia/useCitologia";
import SearchableSelect from "../../components/Common/SearchableSelect";
import {
  Actions,
  AsideTitle,
  Card,
  ContentGrid,
  EmptyText,
  Field,
  Form,
  Grid,
  HeaderBar,
  HistoryItem,
  HistoryList,
  HistoryMeta,
  InfoBox,
  Input,
  Label,
  LoadingText,
  Page,
  Title,
  TitleRule,
  Wrapper,
} from "./Citologia.styles";

const SELECT_SX = {
  width: "100%",
  minWidth: "unset",
  bgcolor: "#ffffff",
  "& .MuiOutlinedInput-root": {
    height: "max(32px, calc(var(--app-font-size, 13px) * 2.3))",
    minHeight: "32px",
    borderRadius: "5px",
    fontSize: "var(--app-font-size, 13px)",
    fontFamily: "var(--font-body)",
    bgcolor: "#ffffff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#c8d1db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94a3b8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#2f86de",
    boxShadow: "0 0 0 2px rgba(47, 134, 222, 0.14)",
  },
};

const SELECT_INNER_SX = {
  height: "max(32px, calc(var(--app-font-size, 13px) * 2.3))",
  minHeight: "32px",
  "& .MuiSelect-select": {
    padding: "5px 10px !important",
    fontSize: "var(--app-font-size, 13px)",
    fontFamily: "var(--font-body)",
    color: "#253244",
  },
};

const toDisplayDate = (value) => {
  if (!value) return "";
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const buildOptionLabel = (item) => {
  const code = item.codigo ?? item.Codigo ?? item.IdLista ?? item.id_catalogo ?? item.Id_usuario ?? "";
  const label = item.descripcion ?? item.Valor ?? item.nombre ?? item.Nombre ?? "";
  return code ? `${code}. ${label}` : label;
};

const buildAspectoCuelloLabel = (item) => {
  return (item.descripcion ?? "").replace(/^\d+[\s.]*/, "").trim();
};


const buildTopValue = (label, fallback = "") => {
  if (label) return String(label);
  return fallback;
};

const getTodayIsoDate = () => {
  const now = new Date();
  const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

export default function Citologia() {
  const {
    catalogos,
    departments,
    form,
    isHisterectomiaSi,
    historico,
    loadingCatalogos,
    loadingHistorico,
    loadingLocation,
    loadingLookup,
    patient,
    patientAge,
    saving,
    canEditCurrentRecord,
    resetForm,
    setField,
    handleDateBlur,
    loadDetalle,
    submit,
    towns,
  } = useCitologia();

  return (
    <DivContainer>
      <Page>
        <Wrapper>
          <ContentGrid>
            <Card>
              <HeaderBar>
                <Title>CAPTURA DE DATOS DE CITOLOGÍAS</Title>
                <BtnGeneral
                  as="button"
                  type="button"
                  onClick={resetForm}
                  bg="gray"
                  padding="8px 18px"
                  radius="6px"
                  fw="600"
                >
                  Reiniciar
                </BtnGeneral>
              </HeaderBar>
              <TitleRule />

              {loadingCatalogos ? (
                <LoadingText>Cargando catálogos del módulo...</LoadingText>
              ) : (
                <Form onSubmit={(e) => e.preventDefault()}>
                  <Grid>
                    <Field $span={3}>
                      <Label>Código de IPS</Label>
                      <InfoBox>{catalogos?.sede_activa?.codigoSede || ""}</InfoBox>
                    </Field>
                    <Field $span={4}>
                      <Label>Nombre de IPS que Realiza el procedimiento</Label>
                      <InfoBox>{catalogos?.sede_activa?.nombreSede || ""}</InfoBox>
                    </Field>
                    <Field $span={5}>
                      <Label>EPS</Label>
                      <InfoBox>
                        {buildTopValue(form.eps_nombre || patient.eps_nombre, "")}
                      </InfoBox>
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={3}>
                      <Label htmlFor="tipo_id">Tipo ID</Label>
                      <SearchableSelect
                        value={form.tipo_id}
                        onValueChange={(val) => setField("tipo_id", val)}
                        options={catalogos?.tipos_identificacion || []}
                        getOptionValue={(item) => String(item.Codigo)}
                        getOptionLabel={(item) => `${item.Codigo} - ${item.Descripcion}`}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={3}>
                      <Label htmlFor="numero_identificacion">Número de Identificación</Label>
                      <Input
                        id="numero_identificacion"
                        value={form.numero_identificacion}
                        onChange={(e) => setField("numero_identificacion", e.target.value)}
                      />
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={3}>
                      <Label>Primer apellido</Label>
                      <InfoBox>{patient.primer_apellido || ""}</InfoBox>
                    </Field>
                    <Field $span={3}>
                      <Label>Segundo apellido</Label>
                      <InfoBox>{patient.segundo_apellido || ""}</InfoBox>
                    </Field>
                    <Field $span={3}>
                      <Label>Primer nombre</Label>
                      <InfoBox>{patient.primer_nombre || ""}</InfoBox>
                    </Field>
                    <Field $span={3}>
                      <Label>Segundo nombre</Label>
                      <InfoBox>{patient.segundo_nombre || ""}</InfoBox>
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={3}>
                      <Label>Fecha de Nacimiento</Label>
                      <InfoBox>{toDisplayDate(patient.fecha_nacimiento)}</InfoBox>
                    </Field>
                    <Field $span={3}>
                      <Label>Edad (años)</Label>
                      <InfoBox>{patientAge === "" ? "" : `${patientAge}`}</InfoBox>
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={3}>
                      <Label htmlFor="departamento_id">Departamento</Label>
                      <SearchableSelect
                        value={form.departamento_id}
                        onValueChange={(val) => setField("departamento_id", val)}
                        disabled={!patient.autoid || loadingLocation}
                        options={departments}
                        getOptionValue={(item) => String(item.id)}
                        getOptionLabel={(item) => item.descripcion}
                        placeholder="Seleccione departamento"
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={3}>
                      <Label htmlFor="municipio_id">Municipio de residencia</Label>
                      <SearchableSelect
                        value={form.municipio_id}
                        onValueChange={(val) => setField("municipio_id", val)}
                        disabled={!patient.autoid || !form.departamento_id || loadingLocation}
                        options={towns}
                        getOptionValue={(item) => String(item.codigo)}
                        getOptionLabel={(item) => item.descripcion}
                        placeholder="Seleccione municipio"
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={4}>
                      <Label htmlFor="direccion_paciente">Dirección</Label>
                      <Input
                        id="direccion_paciente"
                        value={form.direccion_paciente}
                        onChange={(e) => setField("direccion_paciente", e.target.value)}
                        disabled={!patient.autoid}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label>Teléfono</Label>
                      <InfoBox>{patient.telefono || ""}</InfoBox>
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={2}>
                      <Label htmlFor="num_citologias_previas">N° de Citologías previas</Label>
                      <Input
                        id="num_citologias_previas"
                        type="number"
                        min="0"
                        max="30"
                        step="1"
                        value={form.num_citologias_previas}
                        onChange={(e) => setField("num_citologias_previas", e.target.value)}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="fecha_ultima_citologia">Fecha última citología</Label>
                      <Input
                        id="fecha_ultima_citologia"
                        type="date"
                        max={getTodayIsoDate()}
                        value={form.fecha_ultima_citologia}
                        onChange={(e) => setField("fecha_ultima_citologia", e.target.value, e)}
                        onBlur={(e) => handleDateBlur("fecha_ultima_citologia", e)}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="histerectomia_id">Histerectomía</Label>
                      <SearchableSelect
                        value={form.histerectomia_id}
                        onValueChange={(val) => setField("histerectomia_id", val)}
                        options={catalogos?.respuesta_binaria || []}
                        getOptionValue={(item) => String(item.id_catalogo)}
                        getOptionLabel={(item) => item.descripcion}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="aspecto_cuello_id">Aspecto del Cuello</Label>
                      <SearchableSelect
                        value={form.aspecto_cuello_id}
                        disabled={isHisterectomiaSi}
                        onValueChange={(val) => setField("aspecto_cuello_id", val)}
                        options={catalogos?.aspecto_cuello || []}
                        getOptionValue={(item) => String(item.id_catalogo)}
                        getOptionLabel={buildAspectoCuelloLabel}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="planifica_id">Planifica</Label>
                      <SearchableSelect
                        value={form.planifica_id}
                        disabled={isHisterectomiaSi}
                        onValueChange={(val) => setField("planifica_id", val)}
                        options={catalogos?.respuesta_binaria || []}
                        getOptionValue={(item) => String(item.id_catalogo)}
                        getOptionLabel={(item) => item.descripcion}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="tamizaje_mama_id">Tamizaje Ca de Mama</Label>
                      <SearchableSelect
                        value={form.tamizaje_mama_id}
                        onValueChange={(val) => setField("tamizaje_mama_id", val)}
                        options={catalogos?.respuesta_binaria || []}
                        getOptionValue={(item) => String(item.id_catalogo)}
                        getOptionLabel={(item) => item.descripcion}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={2}>
                      <Label htmlFor="fecha_toma">Fecha toma de Muestra</Label>
                      <Input
                        id="fecha_toma"
                        type="date"
                        max={getTodayIsoDate()}
                        value={form.fecha_toma}
                        onChange={(e) => setField("fecha_toma", e.target.value, e)}
                        onBlur={(e) => handleDateBlur("fecha_toma", e)}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="fecha_envio">Fecha envío a lab</Label>
                      <Input
                        id="fecha_envio"
                        type="date"
                        min={form.fecha_toma || undefined}
                        max={getTodayIsoDate()}
                        value={form.fecha_envio}
                        onChange={(e) => setField("fecha_envio", e.target.value, e)}
                        onBlur={(e) => handleDateBlur("fecha_envio", e)}
                      />
                    </Field>
                    <Field $span={2}>
                      <Label htmlFor="fecha_resultado">Fecha resultado</Label>
                      <Input
                        id="fecha_resultado"
                        type="date"
                        min={form.fecha_envio || undefined}
                        max={getTodayIsoDate()}
                        value={form.fecha_resultado}
                        onChange={(e) => setField("fecha_resultado", e.target.value, e)}
                        onBlur={(e) => handleDateBlur("fecha_resultado", e)}
                      />
                    </Field>
                    <Field $span={3}>
                      <Label htmlFor="resultado_id">Resultado</Label>
                      <SearchableSelect
                        value={form.resultado_id}
                        onValueChange={(val) => setField("resultado_id", val)}
                        options={catalogos?.resultado || []}
                        getOptionValue={(item) => String(item.id_catalogo)}
                        getOptionLabel={buildOptionLabel}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={3}>
                      <Label htmlFor="fecha_entrega">Fecha entrega a paciente</Label>
                      <Input
                        id="fecha_entrega"
                        type="date"
                        min={form.fecha_resultado || undefined}
                        max={getTodayIsoDate()}
                        value={form.fecha_entrega}
                        onChange={(e) => setField("fecha_entrega", e.target.value, e)}
                        onBlur={(e) => handleDateBlur("fecha_entrega", e)}
                      />
                    </Field>
                  </Grid>

                  <Grid>
                    <Field $span={6}>
                      <Label htmlFor="persona_procedimiento_id">Persona que realiza el procedimiento</Label>
                      <SearchableSelect
                        value={form.persona_procedimiento_id}
                        onValueChange={(val) => setField("persona_procedimiento_id", val)}
                        options={catalogos?.usuarios_procedimiento || []}
                        getOptionValue={(item) => String(item.Id_usuario)}
                        getOptionLabel={(item) => item.nombre}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                    <Field $span={6}>
                      <Label htmlFor="operador_id">Operador</Label>
                      <SearchableSelect
                        value={form.operador_id}
                        onValueChange={(val) => setField("operador_id", val)}
                        options={catalogos?.usuarios_procedimiento || []}
                        getOptionValue={(item) => String(item.Id_usuario)}
                        getOptionLabel={(item) => item.nombre}
                        placeholder="Digitar valor..."
                        multiple={false}
                        searchable={true}
                        sx={SELECT_SX}
                        selectSx={SELECT_INNER_SX}
                      />
                    </Field>
                  </Grid>

                  <Actions>
                    <BtnGeneral
                      as="button"
                      type="button"
                      onClick={resetForm}
                      bg="gray"
                      padding="8px 18px"
                      radius="6px"
                      fw="600"
                    >
                      Reiniciar
                    </BtnGeneral>
                    <BtnGeneral
                      as="button"
                      type="button"
                      onClick={submit}
                      bg="blue"
                      padding="8px 18px"
                      radius="6px"
                      fw="600"
                      disabled={saving || loadingLookup || !canEditCurrentRecord}
                    >
                      {saving ? "Guardando..." : "Guardar Registro"}
                    </BtnGeneral>
                  </Actions>
                </Form>
              )}
            </Card>

            <Card>
              <AsideTitle>Histórico del paciente</AsideTitle>

              {loadingLookup && <LoadingText>Buscando paciente...</LoadingText>}
              {loadingHistorico && <LoadingText>Cargando histórico...</LoadingText>}

              {!patient.autoid && !loadingLookup && (
                <EmptyText>Identifica un paciente para consultar registros previos.</EmptyText>
              )}

              {!!patient.autoid && !loadingHistorico && historico.length === 0 && (
                <EmptyText>No hay registros previos de citología para este paciente.</EmptyText>
              )}

              <HistoryList>
                {historico.map((item) => (
                  <HistoryItem type="button" key={item.id_citologia} onClick={() => loadDetalle(item.id_citologia)}>
                    <strong>Registro #{item.id_citologia}</strong>
                    <HistoryMeta>Fecha toma: {toDisplayDate(item.fecha_toma)}</HistoryMeta>
                    <HistoryMeta>Resultado: {item.resultado_descripcion || "Sin resultado"}</HistoryMeta>
                    <HistoryMeta>Profesional: {item.persona_procedimiento_nombre || "Sin dato"}</HistoryMeta>
                  </HistoryItem>
                ))}
              </HistoryList>
            </Card>
          </ContentGrid>
        </Wrapper>
      </Page>
    </DivContainer>
  );
}
